import React from 'react';
import { useTranslation } from 'react-i18next';

export interface EmojiItem {
  code: string;
  emoji: string;
  title: string;
}

export const EMOJIS: EmojiItem[] = [
  { code: ':)', emoji: '😊', title: 'Grinning' },
  { code: ':D', emoji: '😁', title: 'Beaming' },
  { code: ';)', emoji: '😉', title: 'Winking' },
  { code: ':p', emoji: '😛', title: 'Savoring' },
  { code: ':><', emoji: '😆', title: 'Squinting' },
  { code: ':O', emoji: '😮', title: 'Astonished' },
  { code: ":D'", emoji: "😅", title: "Grinning Sweat" },
  { code: ':rolf', emoji: '😂', title: 'Tears of Joy' },
  { code: ':lol', emoji: '🤣', title: 'Laughing out Loud' },
  { code: '(:', emoji: '🙃', title: 'Silly' },
  { code: ':8-', emoji: '🙄', title: 'Eye Roll' },
  { code: '*-)', emoji: '🤔', title: 'Thinking' },
  { code: ':8', emoji: '😎', title: 'Cool' },
  { code: ':*D', emoji: '🤩', title: 'Excited' },
  { code: '(A)', emoji: '😇', title: 'Angel' },
  { code: ':hug', emoji: '🤗', title: 'Hug' },
  { code: '^^', emoji: '😊', title: 'Happy' },
  { code: ':*', emoji: '😗', title: 'Kiss' },
  { code: ';*', emoji: '😘', title: 'Kissing' },
  { code: '(L)', emoji: '😍', title: 'Heart Eyes' },
  { code: '(iL)', emoji: '🥰', title: 'In Love' },
  { code: '<3', emoji: '❤️', title: 'Love' },
  { code: '</3', emoji: '💔', title: 'Broken Love' },
  { code: '>:(', emoji: '😡', title: 'Pouting' },
  { code: ':@', emoji: '😠', title: 'Angry' },
  { code: ':#', emoji: '🤐', title: 'Symbol on Mouth' },
  { code: ':/@', emoji: '😤', title: 'Steaming' },
  { code: '=*', emoji: '🥺', title: 'Pleading' },
  { code: ':$', emoji: '😳', title: 'Flushed' },
  { code: ':meh', emoji: '😒', title: 'Unamused' },
  { code: ':wtf', emoji: '😨', title: 'Fearful' },
  { code: ':omg', emoji: '😱', title: 'Scream' },
  { code: ':C', emoji: '😢', title: 'Sad' },
  { code: ":'", emoji: '😭', title: 'Cry' },
  { code: ':zzz', emoji: '😴', title: 'Sleeping' },
  { code: ':|', emoji: '🤫', title: 'Zip it' },
  { code: ':hush', emoji: '🤭', title: 'Shushing' },
  { code: ':_', emoji: '😑', title: 'Blank' },
  { code: ':,', emoji: '🤤', title: 'Drool' },
  { code: ":p'", emoji: '🥵', title: 'Hot face' },
  { code: ':%', emoji: '🥶', title: 'Freeze' },
  { code: '#:0', emoji: '🤯', title: 'Mind blown' },
  { code: ':>_<', emoji: '🤢', title: 'Throwing up' },
  { code: ':party', emoji: '🥳', title: 'Party' },
  { code: '(sne)', emoji: '🙈', title: 'See no Evil' },
  { code: ':2h', emoji: '🙌', title: 'Raising Hands' },
  { code: ':yes', emoji: '👍', title: 'Thumbs up' },
  { code: ':no', emoji: '👎', title: 'Thumbs down' },
  { code: ':up', emoji: '☝️', title: 'Point up' },
  { code: ':peace', emoji: '✌️', title: 'Peace' },
  { code: 'm/', emoji: '🤘', title: 'Rock on' },
  { code: ':muscle', emoji: '💪', title: 'Muscle' },
  { code: ':stop', emoji: '✋', title: 'High Five' },
  { code: ':pray', emoji: '🙏', title: 'Folded hands' },
  { code: ':clap', emoji: '👏', title: 'Clapping hand' },
  { code: ':write', emoji: '✍️', title: 'Writing Hand' },
];

interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
  onClose: () => void;
  style?: React.CSSProperties;
}

export const EmojiPicker: React.FC<EmojiPickerProps> = ({ onSelect, onClose, style }) => {
  const { t } = useTranslation('common');
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClick);
    }, 10);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      style={style}
      className="bg-white border border-gray-200 rounded-lg shadow-xl p-3 w-72"
    >
      <p className="text-xs text-gray-400 mb-2 font-medium">{t('editor.insertEmoji')}</p>
      <div className="grid grid-cols-8 gap-0.5">
        {EMOJIS.map((item) => (
          <button
            key={item.code}
            type="button"
            title={item.title + ' (' + item.code + ')'}
            onMouseDown={(e) => {
              e.preventDefault();
              onSelect(item.emoji);
              onClose();
            }}
            className="text-xl hover:bg-blue-50 rounded p-1.5 transition-colors leading-none cursor-pointer select-none"
          >
            {item.emoji}
          </button>
        ))}
      </div>
    </div>
  );
};